import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../types';

interface Props {
  chatHistory: readonly ChatMessage[];
  onSend: (text: string) => void;
  visible: boolean;
  onToggle: () => void;
}

export function ChatOverlay({ chatHistory, onSend, visible, onToggle }: Props) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    onSend(input.trim());
    setInput('');
  }, [input, onSend]);

  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [visible]);

  // Global Enter key to toggle chat
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !visible) {
        e.preventDefault();
        onToggle();
      }
      if (e.key === 'Escape' && visible) {
        onToggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, onToggle]);

  if (!visible) return null;

  return (
    <div className="absolute bottom-4 left-4 z-50 w-80 rounded-lg border border-border bg-panel-scrim text-foreground shadow-lg backdrop-blur">
      <div className="flex items-center justify-between border-b border-border px-3 py-1">
        <span className="text-xs text-muted-foreground">Chat</span>
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={onToggle}
        >
          ✕
        </button>
      </div>
      <div className="max-h-40 overflow-y-auto p-2">
        {chatHistory.map((msg, i) => (
          <p key={`${msg.timestamp}-${i}`} className="mb-0.5 text-xs text-foreground">
            <span className="font-bold text-sky-700 dark:text-sky-300">{msg.sender}</span>:{' '}
            {msg.text}
          </p>
        ))}
      </div>
      <div className="flex border-t border-border p-1">
        <input
          ref={inputRef}
          className="flex-1 rounded-l bg-background px-2 py-1 text-xs text-foreground outline-none"
          placeholder="Chat…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <button
          type="button"
          className="rounded-r bg-sky-600 px-2 py-1 text-xs text-white hover:bg-sky-500"
          onClick={handleSend}
        >
          Send
        </button>
      </div>
    </div>
  );
}
